// Dev-time cost guards. Warn (or throw, if strict) when an SDK
// operation is about to scan a lot of objects or fetch many docs.
// Catches accidental full-table scans early — apps SHOULD add a
// secondary index instead.
//
// Guards are off by default in production builds. Enable via:
//
//   import { setDevGuards } from "/sdk/index.js";
//   setDevGuards({ enabled: true });
//
// or by running under `s3p dev`, which sets a window-level flag.

export interface DevGuardsConfig {
  enabled: boolean;
  /** Above this object count, emit a warning. Default 500. */
  warnAtCount?: number;
  /** Above this object count, throw instead of warn. Default 5000. */
  throwAtCount?: number;
  /** Receives the warnings. Defaults to console.warn. */
  emit?: (msg: string) => void;
}

let cfg: Required<DevGuardsConfig> = {
  enabled: detectDevMode(),
  warnAtCount: 500,
  throwAtCount: 5000,
  emit: (m) => {
    // eslint-disable-next-line no-console
    console.warn(`[s3p devGuards] ${m}`);
  },
};

export function setDevGuards(next: Partial<DevGuardsConfig>): void {
  cfg = { ...cfg, ...next } as Required<DevGuardsConfig>;
}

export function getDevGuards(): Required<DevGuardsConfig> {
  return cfg;
}

/** Notes a scan and emits a warning or throws if over the threshold.
 *  Pass the op shape ("scan", "list", etc.) and the collection or
 *  prefix being walked for a useful diagnostic. */
export function reportScanCount(op: string, source: string, count: number): void {
  if (!cfg.enabled) return;
  if (count >= cfg.throwAtCount) {
    throw new Error(
      `[s3p devGuards] ${op}('${source}') scanned ${count} objects (limit ${cfg.throwAtCount}). Add a secondary index or paginate.`,
    );
  }
  if (count >= cfg.warnAtCount) {
    cfg.emit(
      `${op}('${source}') scanned ${count} objects. Consider adding a secondary index — see app.collection({ indexes: [...] }).`,
    );
  }
}

function detectDevMode(): boolean {
  // `s3p dev` sets this at boot.
  if (typeof globalThis !== "undefined" && (globalThis as { __s3p_dev?: boolean }).__s3p_dev) {
    return true;
  }
  // Hostname heuristic — localhost / 127.0.0.1 → dev.
  if (typeof window !== "undefined" && window.location) {
    const h = window.location.hostname;
    return h === "localhost" || h === "127.0.0.1" || h.endsWith(".local");
  }
  return false;
}
